import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sensor, SensorReading, WebSocketMessage } from '../types';
import { sensorService } from '../services';
import { useEffect } from 'react';
import { useWebSocket } from './useWebSocket';

export const useSensors = () => {
  const queryClient = useQueryClient();
  // POZOR: poslouchat na ŽIVÉM globalWebSocket (hooks/useWebSocket), ne na mrtvém
  // services webSocketService → hydroponicWebSocket (ten je null, handler se nepřipojí).
  const { on, off, subscribe, isConnected } = useWebSocket();

  const {
    data: sensors = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['sensors'],
    queryFn: async () => {
      // Fetch sensors and latest readings in parallel
      const [sensorsData, readingsData] = await Promise.all([
        sensorService.getAllSensors(),
        sensorService.getLatestReadings().catch(() => []), // Silently fail if readings can't be fetched
      ]);

      // Create a map of latest readings for easy lookup
      const readingsMap = new Map<string, SensorReading>();
      if (Array.isArray(readingsData)) {
        readingsData.forEach((reading) => {
          if (reading && reading.sensorId) {
            readingsMap.set(reading.sensorId, reading);
          }
        });
      }

      // Merge sensors with their latest readings
      return sensorsData.map((sensorData: any) => {
        // Map backend snake_case fields to frontend camelCase if needed
        const sensor = {
          ...sensorData,
          minValue:
            sensorData.minValue ??
            sensorData.min_value ??
            sensorData.threshold_min ??
            sensorData.threshold?.min,
          maxValue:
            sensorData.maxValue ??
            sensorData.max_value ??
            sensorData.threshold_max ??
            sensorData.threshold?.max,
        };

        const latestReading = readingsMap.get(sensor.id);
        if (latestReading) {
          return {
            ...sensor,
            value: latestReading.value,
            last_reading_at:
              latestReading.timestamp instanceof Date
                ? latestReading.timestamp.toISOString()
                : latestReading.timestamp,
            status: 'active' as const,
          };
        }
        return sensor;
      });
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // 1 minute
  });

  // Real-time WS: aktualizace valid flagu pro odpojenou sondu
  useEffect(() => {
    const handleSensorData = (message: WebSocketMessage) => {
      if (message.type !== 'sensor_data' || typeof message.data !== 'object' || !message.data)
        return;
      const wsData = message.data as Record<string, unknown>;
      if (wsData['valid'] === undefined) return;

      const sensorType = wsData['sensorType'] as string;
      const valid = wsData['valid'] as boolean;
      const value = wsData['value'] as number;
      const baseValue = wsData['baseValue'] as number | undefined;
      const timestamp = wsData['timestamp'] as string;

      queryClient.setQueryData(['sensors'], (prev: Sensor[] | undefined) => {
        if (!prev) return prev;
        return prev.map((s) => {
          if (s.type === sensorType) {
            return {
              ...s,
              valid,
              ...(valid
                ? { value, last_reading_at: timestamp, ...(baseValue !== undefined ? { baseValue } : {}) }
                : {}),
            };
          }
          return s;
        });
      });
    };

    on('sensor_data', handleSensorData);
    return () => {
      off('sensor_data', handleSensorData);
    };
    // on/off jsou stabilní (delegují na modulový globalWebSocket)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  // KLÍČOVÉ: přihlásit se k odběru zařízení svých senzorů. Server (broadcastToDevice)
  // posílá sensor_data JEN klientům v subscribedDeviceIds. globalWebSocket se připojuje
  // s deviceId='' → bez tohoto subscribe by nikdy nedorazila žádná live data.
  const deviceKey = Array.from(
    new Set(
      sensors
        .map((s) => (s as unknown as { device_id?: string }).device_id)
        .filter((d): d is string => !!d),
    ),
  )
    .sort()
    .join(',');
  useEffect(() => {
    if (!isConnected || !deviceKey) return;
    deviceKey.split(',').forEach((id) => subscribe('sensor_data', id));
    // subscribe je idempotentní (server používá Set); neodhlašujeme při unmountu,
    // aby se nerozbil odběr pro ostatní komponenty na sdíleném socketu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, deviceKey]);

  return {
    sensors,
    loading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refetch,
  };
};

export const useSensor = (sensorId: string) => {
  const queryClient = useQueryClient();
  const { on, off } = useWebSocket();

  const {
    data: sensor,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['sensor', sensorId],
    queryFn: () => sensorService.getSensorById(sensorId),
    enabled: !!sensorId,
    staleTime: 30000,
  });

  useEffect(() => {
    if (!sensorId) return;

    // Subscribe to real-time updates for this sensor
    const handleSensorUpdate = (message: WebSocketMessage) => {
      if (
        message.type === 'sensor_data' &&
        typeof message.data === 'object' &&
        message.data !== null &&
        'id' in message.data &&
        (message.data as { id: string }).id === sensorId
      ) {
        queryClient.setQueryData(['sensor', sensorId], (prev: Sensor | undefined) =>
          prev ? { ...prev, ...(message.data as Record<string, unknown>) } : null,
        );
      }
    };

    on('sensor_data', handleSensorUpdate);

    return () => {
      off('sensor_data', handleSensorUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorId, queryClient]);

  return {
    sensor: sensor || null,
    loading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
  };
};

export const useSensorReadings = (sensorId: string, startDate?: Date, endDate?: Date) => {
  const {
    data: readings = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['sensor-readings', sensorId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () =>
      sensorService.getSensorHistory(
        sensorId,
        startDate ? startDate.toISOString() : new Date(0).toISOString(),
        endDate ? endDate.toISOString() : new Date().toISOString(),
      ),
    enabled: !!sensorId,
    staleTime: 60000, // 1 minute
  });

  return {
    readings,
    loading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
  };
};

export const useLatestSensorReadings = () => {
  const {
    data: readings = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['latest-sensor-readings'],
    queryFn: async () => {
      // Prepared for ESP32 API integration
      // When ESP32 backend is available, replace with actual API call:
      // return await sensorService.getLatestReadings();
      return [];
    },
    staleTime: 30000,
  });

  return {
    readings,
    loading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
  };
};
